import React, { useState, useEffect, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LabelList, ResponsiveContainer } from 'recharts';
import { useChowaTheme } from '../config/chowaThemeContext';
import { commonStyles } from '../config/ChowaDashboardLayout';
import classNames from 'classnames';
import Card from '../config/ChowaCard';
import NoInlineCard from '../config/ChowaNoInlineCard';
import Table from '../config/ChowaTable';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { HiOutlineSearch, HiOutlineX } from 'react-icons/hi';
import RomanNumeralText from '../components/RomanNumeralText';

// Swiper CSS
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

// カスタムツールチップ
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const item = payload[0].payload;
    return (
      <div style={{ background: '#fff', border: '1px solid #ccc', borderRadius: 8, padding: 10, fontFamily: 'Segoe UI, Roboto, Arial, sans-serif' }}>
        <table>
          <tbody>
            <tr>
              <td style={{ fontWeight: 'bold', paddingRight: 8 }}>CustomUnitName:</td>
              <td>{item.customData || '-'}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 'bold', paddingRight: 8 }}>Unit Serial:</td>
              <td>{item.unitSerialNumber && item.unitSerialNumber !== '' ? item.unitSerialNumber : '-'}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 'bold', paddingRight: 8 }}>Device ID:</td>
              <td>{item.deviceId && item.deviceId !== '' ? item.deviceId : '-'}</td>
            </tr>
                        <tr>
              <td style={{ fontWeight: 'bold', paddingRight: 8 }}>Last Update:</td>
              <td>
                <div style={{
                  color: item.isCanError ? '#f59e0b' : 'inherit',
                  whiteSpace: 'nowrap'
                }}>
                  {item.timestamp ? new Date(item.timestamp).toLocaleString('ja-JP') : '-'}
                </div>
                {item.latestIsError && item.hasValidSecondData && (
                  <div style={{ 
                    color: 'inherit',
                    whiteSpace: 'nowrap',
                    marginTop: '2px'
                  }}>
                    {new Date(item.secondTimestamp).toLocaleString('ja-JP')}
                  </div>
                )}
              </td>
            </tr>
            <tr>
              <td style={{ fontWeight: 'bold', paddingRight: 8 }}>Operating Time:</td>
              <td>{item.hours !== undefined && item.hours !== null && item.hours !== '' && item.hours > 0 ? Number(item.hours).toFixed(1) + 'h' : '-'}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }
  return null;
};

const TimeEngineHoursBarChart = ({ data, seriesColor, selectedModel }) => {
  const { isDark, getCurrentTheme } = useChowaTheme();
  const currentTheme = getCurrentTheme();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleButtonIndices, setVisibleButtonIndices] = useState([0, 1, 2, 3, 4]); // 表示されているボタンのインデックス
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef(null);

  // ページネーションの色（現在のテーマカラーを使用）
  const getPaginationColor = () => {
    return currentTheme.primary;
  };

  // チャートバーの色（propsのseriesColor優先、なければテーマカラー）
  const barColor = seriesColor || currentTheme.primary;

  // シリーズ色名を決定
  const getTableColorName = (color) => {
    if (!color) return 'gray';
    if (color.startsWith('#3b82f6')) return 'blue';
    if (color.startsWith('#f97316')) return 'orange';
    if (color.startsWith('#9333ea')) return 'purple';
    return 'gray';
  };
  const tableColor = getTableColorName(seriesColor);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // データが変更された時にvisibleButtonIndicesをリセット
  useEffect(() => {
    if (data && data.length > 5) {
      const step = 10;
      const totalButtons = Math.ceil(data.length / step) + 1; // +1は最初の「1」ボタンのため
      const maxButtons = Math.min(5, totalButtons);
      const initialIndices = Array.from({ length: maxButtons }, (_, i) => i);
      setVisibleButtonIndices(initialIndices);
    }
  }, [data]);

  // 検索でフィルタリングされたデータ
  const filteredData = data ? data.filter(item => 
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  // 検索クリア機能
  const clearSearch = () => {
    setSearchTerm('');
  };

  // データの最初のModel名から#番号を抽出
  const getStartNumber = () => {
    if (!data || data.length === 0) return 1;
    const match = data[0].name && data[0].name.match(/#(\d+)$/);
    return match ? parseInt(match[1], 10) : 1;
  };
  const startNumber = getStartNumber();

  // スキップボタンの生成（<< + 5個 + >>ボタン）
  const generateSkipButtons = () => {
    const buttons = [];
    const step = 10;
    
    // 検索中は表示しない
    if (searchTerm) return buttons;
    
    // データが5個以下の場合は表示しない
    if (!data || data.length <= 5) return buttons;
    
    // 利用可能なボタンの総数を計算
    const totalButtons = Math.ceil(data.length / step) + 1; // +1は最初のボタンのため
    
    // <<ボタンを追加（最初の位置でない場合）
    if (visibleButtonIndices[0] > 0) {
      buttons.push(
        <button
          key="prev"
          onClick={() => {
            const newIndices = [...visibleButtonIndices];
            // 各インデックスを1つ左に移動
            newIndices.forEach((_, index) => {
              newIndices[index] = Math.max(0, newIndices[index] - 1);
            });
            setVisibleButtonIndices(newIndices);
          }}
          className={classNames(
            "px-3 py-2 text-sm rounded border transition-colors duration-200 whitespace-nowrap",
            "focus:outline-none focus:ring-0 focus:border-none",
            "active:outline-none active:ring-0 active:border-none",
            "min-w-[40px] text-center"
          )}
          style={{
            backgroundColor: isDark ? seriesColor + '20' : seriesColor + '20',
            color: seriesColor,
            borderColor: "transparent",
            outline: 'none',
            boxShadow: 'none',
          }}
        >
          &lt;&lt;
        </button>
      );
    }
    
    // 現在表示されているボタンを生成
    visibleButtonIndices.forEach((buttonIndex) => {
      if (buttonIndex < totalButtons) {
        const buttonNumber = startNumber + buttonIndex * step;
        const targetDataIndex = buttonNumber - startNumber;
        const targetSlideIndex = Math.floor(targetDataIndex / 5);
        if (targetDataIndex < data.length) {
          const isCurrent = activeIndex === targetSlideIndex;
          buttons.push(
            <button
              key={buttonNumber}
              onClick={() => {
                if (swiperRef.current && swiperRef.current.swiper) {
                  swiperRef.current.swiper.slideTo(targetSlideIndex);
                }
              }}
              className={classNames(
                "px-3 py-2 text-sm rounded border transition-colors duration-200 whitespace-nowrap",
                "focus:outline-none focus:ring-1 focus:ring-black",
                "min-w-[40px] text-center"
              )}
              style={{
                backgroundColor: isCurrent ? seriesColor : (isDark ? "#333" : "#eee"),
                color: isCurrent ? "white" : (isDark ? "#fff" : "#222"),
                border: "none",
                borderColor: "transparent",
                outline: 'none',
                boxShadow: 'none',
              }}
            >
              {buttonNumber}
            </button>
          );
        }
      }
    });
    
    // >>ボタン
    const lastVisibleIndex = visibleButtonIndices[visibleButtonIndices.length - 1];
    const isNextDisabled = lastVisibleIndex >= totalButtons - 1;
    buttons.push(
      <button
        key="next"
        onClick={() => {
          if (isNextDisabled) return;
          const newIndices = [...visibleButtonIndices];
          // 各インデックスを1つ右に移動
          newIndices.forEach((_, index) => {
            newIndices[index] = newIndices[index] + 1;
          });
          setVisibleButtonIndices(newIndices);
        }}
        className={classNames(
          "px-3 py-2 text-sm rounded border transition-colors duration-200 whitespace-nowrap",
          "focus:outline-none focus:ring-1 focus:ring-black",
          "min-w-[40px] text-center",
          {
            "opacity-50 cursor-not-allowed": isNextDisabled,
          }
        )}
        style={{
          backgroundColor: isNextDisabled
            ? (isDark ? "#444" : "#eee")
            : (isDark ? seriesColor + '20' : seriesColor + '20'),
          color: isNextDisabled
            ? "#aaa"
            : seriesColor,
          borderColor: "transparent",
          outline: 'none',
          boxShadow: 'none',
        }}
        disabled={isNextDisabled}
      >
        &gt;&gt;
      </button>
    );
    
    return buttons;
  };

  if (!data || data.length === 0) {
    return (
      <NoInlineCard className={commonStyles.card.padding.default}>
        <div className="flex flex-col items-center justify-center h-[400px]">
          <div className={classNames(commonStyles.text.title, "text-4xl text-gray-400 dark:text-gray-600 mb-2")}>ー</div>
          <div className={classNames(commonStyles.text.subtitle, "text-gray-400 dark:text-gray-600")}>Data not found</div>
        </div>
      </NoInlineCard>
    );
  }

  // データを5個ずつのグループに分割（検索がある場合はフィルタリングされたデータを使用）
  const dataToUse = searchTerm ? filteredData : data;
  const chunkSize = 5;
  const dataChunks = [];
  for (let i = 0; i < dataToUse.length; i += chunkSize) {
    dataChunks.push(dataToUse.slice(i, i + chunkSize));
  }

  //1400以下は斜めにmodel名表示
  const isMobile = windowWidth < 1400;
  
  // 画面幅に応じた列の表示制御
  const isSmallScreen = windowWidth < 1200;

  const xAxisTickProps = {
    fill: isDark ? '#FFFFFF' : '#1F2937',
    fontSize: isMobile ? 15 : 23,
    ...(isMobile && { angle: -45, textAnchor: 'end', dy: 10 }),
  };

  const chartMargin = {
    top: 20,
    right: 20,
    left: 5,
    bottom: isMobile ? 40 : 5,
  };
  
  // 画面幅に応じた列の表示制御（列は削除せず、幅とスタイルを調整）
  const getTableColumns = () => {
    return [
      { 
        header: 'CustomUnitName', 
        accessor: 'customData', 
        cell: (item) => <p className="text-gray-900 dark:text-white">{item.customData || '-'}</p>,
        style: { minWidth: isSmallScreen ? '120px' : '150px', maxWidth: '200px' }
      },
      { 
        header: 'Unit Serial', 
        accessor: 'unitSerialNumber', 
        cell: (item) => <p className="text-xs text-gray-900 dark:text-white">{item.unitSerialNumber && item.unitSerialNumber !== '' ? item.unitSerialNumber : '-'}</p>,
        style: { minWidth: isSmallScreen ? '80px' : '100px', maxWidth: '120px' }
      },
      { 
        header: 'Device ID', 
        accessor: 'deviceId', 
        cell: (item) => <p className="text-xs text-gray-900 dark:text-white">{item.deviceId && item.deviceId !== '' ? item.deviceId : '-'}</p>,
        style: { minWidth: isSmallScreen ? '80px' : '100px', maxWidth: '120px' }
      },
      { 
        header: 'Last Update', 
        accessor: 'timestamp', 
        cell: (item) => {
          return (
            <div style={{ lineHeight: '1.2' }}>
              <div style={{ 
                color: item.isCanError ? '#f59e0b' : 'inherit',
                whiteSpace: isSmallScreen ? 'normal' : 'nowrap',
                fontSize: isSmallScreen ? '0.75rem' : '0.875rem'
              }}>
                {item.timestamp ? new Date(item.timestamp).toLocaleString('ja-JP') : '-'}
              </div>
              {item.latestIsError && item.hasValidSecondData && (
                <div style={{ 
                  color: 'inherit',
                  whiteSpace: isSmallScreen ? 'normal' : 'nowrap',
                  marginTop: '2px',
                  fontSize: isSmallScreen ? '0.75rem' : '0.875rem'
                }}>
                  {new Date(item.secondTimestamp).toLocaleString('ja-JP')}
                </div>
              )}
            </div>
          );
        },
        style: { minWidth: isSmallScreen ? '120px' : '140px', maxWidth: '160px' }
      },
      { 
        header: 'Operating Time', 
        accessor: 'hours', 
        cell: (item) => <p className="text-gray-900 dark:text-white font-semibold text-base">{
          item.hours !== undefined && item.hours !== null && item.hours !== '' && item.hours > 0 
            ? Number(item.hours).toFixed(1) + ' [h]' 
            : '-'
        }</p>,
        style: { minWidth: '80px', maxWidth: '100px' }
      },

    ];
  };

  const tableColumns = getTableColumns();

  const renderMobileRow = (item) => (
    <div className="grid grid-cols-1 gap-y-3 text-sm">
      {/* ヘッダー */}
      <p className="font-semibold text-base text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-600 pb-2">
        {item.customData || item.name}
      </p>
      
      {/* 基本情報 - 2列グリッド */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        <p className="text-gray-500 dark:text-gray-400">Unit Serial:</p>
        <p className="text-right text-gray-800 dark:text-white">{item.unitSerialNumber || '-'}</p>
        <p className="text-gray-500 dark:text-gray-400">Device ID:</p>
        <p className="text-right text-gray-800 dark:text-white">{item.deviceId || '-'}</p>
        <p className="text-gray-500 dark:text-gray-400">Operating Time:</p>
        <p className="text-right text-gray-800 dark:text-white font-semibold">
          {item.hours !== undefined && item.hours !== null && item.hours !== '' && item.hours > 0 
            ? Number(item.hours).toFixed(1) + 'h' 
            : '-'
          }
        </p>
      </div>
      
      {/* Last Update情報 - 1列で折り返し対応 */}
      <div className="space-y-2">
        <p className="text-gray-500 dark:text-gray-400">Last Update:</p>
        <div className="text-gray-800 dark:text-white text-right">
          <div style={{ 
            color: item.isCanError ? '#f59e0b' : 'inherit',
            whiteSpace: 'normal',
            fontSize: '0.875rem',
            wordBreak: 'break-word'
          }}>
            {item.timestamp ? new Date(item.timestamp).toLocaleString('ja-JP') : '-'}
          </div>
          {item.latestIsError && item.hasValidSecondData && (
            <div style={{ 
              color: 'inherit',
              whiteSpace: 'normal',
              marginTop: '4px',
              fontSize: '0.875rem',
              wordBreak: 'break-word'
            }}>
              {new Date(item.secondTimestamp).toLocaleString('ja-JP')}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // 検索結果が空の場合の表示
  if (searchTerm && filteredData.length === 0) {
    return (
      <Card className={commonStyles.layout.section} style={{ borderColor: seriesColor, padding: '20px' }}>
        <h2 className={classNames(commonStyles.text.title, "text-center text-lg sm:text-2xl lg:text-3xl")} style={{ color: seriesColor, marginBottom: '20px', fontWeight: '600' }}><RomanNumeralText text={selectedModel} /> エンジン稼働状況</h2>
        
        {/* 検索フィールド + スキップボタン群 */}
        <div className="mb-6 w-full">
          {/* 検索フィールド - 中央寄せ */}
          <div className="flex justify-center mb-4">
            <div className="relative max-w-md w-full">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                <HiOutlineSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={classNames(
                  "block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-md",
                  "placeholder-gray-500 focus:outline-none focus:placeholder-gray-400",
                  "focus:ring-1 focus:ring-blue-500 focus:border-blue-500",
                  "dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                )}
                placeholder="CustomUnitNameで検索..."
              />
              {searchTerm && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center z-10">
                  <button
                    onClick={clearSearch}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <HiOutlineX className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* スキップボタン群 - グリッド配置（検索時は非表示） */}
          <div className="flex flex-wrap justify-center gap-1 px-2 sm:gap-3 sm:px-4">
            {/* 検索時は表示しない */}
          </div>
        </div>

        <div className={classNames(
          commonStyles.grid.default,
          "md:grid-cols-5 gap-2"
        )}>
          <div className="min-w-0 md:col-span-2">
            <NoInlineCard className={commonStyles.card.padding.default}>
              <div className="flex flex-col items-center justify-center h-[450px]">
                <div className={classNames(commonStyles.text.title, "text-4xl text-gray-400 dark:text-gray-600 mb-2")}>ー</div>
                <div className={classNames(commonStyles.text.subtitle, "text-gray-400 dark:text-gray-600")}>Data not found</div>
              </div>
            </NoInlineCard>
          </div>
          <div className="pl-0 md:pl-2 md:col-span-3">
            <Table 
              columns={tableColumns} 
              data={[]}
              renderMobileRow={() => (
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <p className="col-span-2 font-semibold text-base text-gray-400 dark:text-gray-600">ー</p>
                  <p className="text-gray-500 dark:text-gray-400">Series:</p>
                  <p className="text-right text-gray-400 dark:text-gray-600">ー</p>
                  <p className="text-gray-500 dark:text-gray-400">Unit Serial:</p>
                  <p className="text-right text-gray-400 dark:text-gray-600">ー</p>
                  <p className="text-gray-500 dark:text-gray-400">Device ID:</p>
                  <p className="text-right text-gray-400 dark:text-gray-600">ー</p>
                  <p className="text-gray-500 dark:text-gray-400">Operating Time:</p>
                  <p className="text-right text-gray-400 dark:text-gray-600">ー</p>
                  <p className="text-gray-500 dark:text-gray-400">排ガス:</p>
                  <p className="text-right text-gray-400 dark:text-gray-600">ー</p>
                  <p className="text-gray-500 dark:text-gray-400">Last Update:</p>
                  <p className="text-right text-gray-400 dark:text-gray-600">ー</p>
                </div>
              )}
            />
          </div>
        </div>
      </Card>
    );
  }

  // データが5個以下の場合（または検索結果）は通常表示
  if (dataToUse.length <= 5) {
    return (
      <Card className={commonStyles.layout.section} style={{ borderColor: seriesColor, padding: '20px' }}>
        <h2 className={classNames(commonStyles.text.title, "text-center text-lg sm:text-2xl lg:text-3xl")} style={{ color: seriesColor, marginBottom: '20px', fontWeight: '600' }}><RomanNumeralText text={selectedModel} /> エンジン稼働状況</h2>
        
        {/* 検索フィールド + スキップボタン群 */}
        <div className="mb-6 w-full">
          {/* 検索フィールド - 中央寄せ */}
          <div className="flex justify-center mb-4">
            <div className="relative max-w-md w-full">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                <HiOutlineSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={classNames(
                  "block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-md",
                  "placeholder-gray-500 focus:outline-none focus:placeholder-gray-400",
                  "focus:ring-1 focus:ring-blue-500 focus:border-blue-500",
                  "dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                )}
                placeholder="UNIT名で検索..."
              />
              {searchTerm && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center z-10">
                  <button
                    onClick={clearSearch}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <HiOutlineX className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* スキップボタン群 - 横一列配置（センター揃え） */}
          {!searchTerm && (
            <div className="flex flex-wrap justify-center gap-1 px-2 sm:gap-3 sm:px-4">
              {generateSkipButtons()}
            </div>
          )}
        </div>

        {searchTerm && (
          <div className="mb-4 text-center">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              検索結果: {filteredData.length}件
            </span>
          </div>
        )}

        <div className={classNames(
          commonStyles.grid.default,
          "md:grid-cols-5 gap-2"
        )}>
          <div className="min-w-0 md:col-span-2">
            <ResponsiveContainer 
              width="100%" 
              height={450} 
              className="md:h-[550px] lg:h-[650px]"
              onTouchStart={(e) => {
                // チャートエリアでのタッチイベントを優先
                e.stopPropagation();
              }}
              onTouchMove={(e) => {
                // チャートエリアでのタッチ移動を優先
                e.stopPropagation();
              }}
            >
              <BarChart
                data={dataToUse}
                margin={chartMargin}
                barSize={40}
                className="md:barSize-50 lg:barSize-60"
              >
                <CartesianGrid 
                  strokeDasharray="3 3" 
                  stroke="#374151"
                  opacity={0.1}
                />
                <XAxis 
                  dataKey="name"
                  tick={xAxisTickProps}
                  axisLine={{ stroke: '#4B5563' }}
                  padding={{ left: 30, right: 30 }}
                  className="sm:text-sm"
                  interval={isMobile ? 0 : 'preserveStartEnd'}
                  tickFormatter={(name) => {
                    const match = name && name.match(/#\d+$/);
                    return match ? match[0] : name;
                  }}
                />
                <YAxis 
                  tick={{ fill: isDark ? '#FFFFFF' : '#1F2937', fontSize: 12 }}
                  axisLine={{ stroke: '#4B5563' }}
                  label={{ 
                    value: 'Operating time (h)', 
                    angle: -90, 
                    position: 'insideLeft', 
                    fill: isDark ? '#FFFFFF' : '#1F2937',
                    fontSize: 16,
                    dx: 0,
                    dy: 70,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                  }}
                />
                <Tooltip 
                  content={<CustomTooltip />}
                  contentStyle={{
                    backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
                    border: 'none',
                    borderRadius: '8px',
                    color: isDark ? '#FFFFFF' : '#1F2937',
                    fontSize: '12px'
                  }}
                />
                <Bar 
                  dataKey="hours" 
                  fill={barColor}
                  radius={[4, 4, 0, 0]}
                >
                  <LabelList 
                    dataKey="hours" 
                    position="top" 
                    fill={isDark ? '#FFFFFF' : '#1F2937'}
                    fontSize={12}
                    formatter={(value) => `${Math.floor(value)}h`}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="pl-0 md:pl-2 md:col-span-3">
            <Table 
              columns={tableColumns} 
              data={dataToUse}
              renderMobileRow={renderMobileRow}
              tableColor={tableColor}
            />
          </div>
        </div>
      </Card>
    );
  }

  // データが6個以上で検索なしの場合はカルーセル表示
  return (
    <Card className={commonStyles.layout.section} style={{ borderColor: seriesColor, padding: '20px' }}>
      <h2 className={classNames(commonStyles.text.title, "text-center text-lg sm:text-2xl lg:text-3xl")} style={{ color: seriesColor, marginBottom: '20px', fontWeight: '600' }}><RomanNumeralText text={selectedModel} /> エンジン稼働状況</h2>
      
             {/* 検索フィールド + スキップボタン群 */}
       <div className="mb-6 w-full">
         {/* 検索フィールド - 中央寄せ */}
         <div className="flex justify-center mb-4">
           <div className="relative max-w-md w-full">
             <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
               <HiOutlineSearch className="h-5 w-5 text-gray-400" />
             </div>
             <input
               type="text"
               value={searchTerm}
               onChange={(e) => setSearchTerm(e.target.value)}
               className={classNames(
                 "block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-md",
                 "placeholder-gray-500 focus:outline-none focus:placeholder-gray-400",
                 "focus:ring-1 focus:ring-blue-500 focus:border-blue-500",
                 "dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
               )}
               placeholder="UNIT名で検索..."
             />
             {searchTerm && (
               <div className="absolute inset-y-0 right-0 pr-3 flex items-center z-10">
                 <button
                   onClick={clearSearch}
                   className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                 >
                   <HiOutlineX className="h-5 w-5" />
                 </button>
               </div>
             )}
           </div>
         </div>

         {/* スキップボタン群 - グリッド配置（センター揃え） */}
         {!searchTerm && (
           <div className="flex flex-wrap justify-center gap-1 px-2 sm:gap-3 sm:px-4">
             {generateSkipButtons()}
           </div>
         )}
       </div>
      
      <Swiper
        ref={swiperRef}
        modules={[Navigation, Pagination]}
        spaceBetween={30}
        slidesPerView={1}
        navigation={true}
        pagination={{ clickable: true }}
        className="engine-hours-swiper"
        style={{
          '--swiper-navigation-color': isDark ? '#FFFFFF' : '#1F2937',
          '--swiper-pagination-color': getPaginationColor(),
        }}
        touchRatio={0.8}
        touchAngle={45}
        touchMoveStopPropagation={false}
        preventInteractionOnTransition={true}
        resistance={true}
        resistanceRatio={0.85}
        touchStartPreventDefault={false}
        touchStartForcePreventDefault={false}
        allowTouchMove={true}
        allowSlidePrev={true}
        allowSlideNext={true}
        onTouchStart={(swiper, event) => {
          const target = event.target;
          if (target.closest('.recharts-wrapper') || target.closest('.recharts-tooltip-wrapper')) {
            event.stopPropagation();
            return false;
          }
        }}
        onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
      >
        {dataChunks.map((chunk, index) => (
          <SwiperSlide key={index}>
            <div className={classNames(
              windowWidth <= 888 ? "grid grid-cols-1 gap-4" : commonStyles.grid.default,
              windowWidth <= 888 ? "" : "md:grid-cols-5 gap-2"
            )}>
              <div className={windowWidth <= 888 ? "w-full" : "min-w-0 md:col-span-2"}>
                <ResponsiveContainer 
                  width="100%" 
                  height={450} 
                  className="md:h-[550px] lg:h-[650px]"
                  onTouchStart={(e) => {
                    // チャートエリアでのタッチイベントを優先
                    e.stopPropagation();
                  }}
                  onTouchMove={(e) => {
                    // チャートエリアでのタッチ移動を優先
                    e.stopPropagation();
                  }}
                >
                  <BarChart
                    data={chunk}
                    margin={chartMargin}
                    barSize={40}
                    className="md:barSize-50 lg:barSize-60"
                  >
                    <CartesianGrid 
                      strokeDasharray="3 3" 
                      stroke="#374151"
                      opacity={0.1}
                    />
                    <XAxis 
                      dataKey="name"
                      tick={xAxisTickProps}
                      axisLine={{ stroke: '#4B5563' }}
                      padding={{ left: 30, right: 30 }}
                      className="sm:text-sm"
                      interval={isMobile ? 0 : 'preserveStartEnd'}
                      tickFormatter={(name) => {
                        const match = name && name.match(/#\d+$/);
                        return match ? match[0] : name;
                      }}
                    />
                    <YAxis 
                      tick={{ fill: isDark ? '#FFFFFF' : '#1F2937', fontSize: 12 }}
                      axisLine={{ stroke: '#4B5563' }}
                      label={{ 
                        value: 'Operating time (h)', 
                        angle: -90, 
                        position: 'insideLeft', 
                        fill: isDark ? '#FFFFFF' : '#1F2937',
                        fontSize: 16,
                        dx: 0,
                        dy: 70,
                        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                      }}
                    />
                    <Tooltip 
                      content={<CustomTooltip />}
                      contentStyle={{
                        backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
                        border: 'none',
                        borderRadius: '8px',
                        color: isDark ? '#FFFFFF' : '#1F2937',
                        fontSize: '12px'
                      }}
                    />
                    <Bar 
                      dataKey="hours" 
                      fill={barColor}
                      radius={[4, 4, 0, 0]}
                    >
                                              <LabelList 
                        dataKey="hours" 
                        position="top" 
                        fill={isDark ? '#FFFFFF' : '#1F2937'}
                        fontSize={12}
                        formatter={(value) => `${Math.floor(value)}h`}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className={windowWidth <= 888 ? "w-full" : "pl-0 md:pl-2 md:col-span-3"}>
                <Table 
                  columns={tableColumns} 
                  data={chunk}
                  renderMobileRow={renderMobileRow}
                  tableColor={tableColor}
                />
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </Card>
  );
};

export default TimeEngineHoursBarChart;

// Swiperのカスタムスタイル
const swiperStyles = `
  .engine-hours-swiper {
    padding-bottom: 50px;
  }
  
  .engine-hours-swiper .swiper-button-next,
  .engine-hours-swiper .swiper-button-prev {
    background-color: rgba(0, 0, 0, 0.1);
    border-radius: 50%;
    width: 40px;
    height: 40px;
    margin-top: -20px;
  }
  
  .engine-hours-swiper .swiper-button-next:hover,
  .engine-hours-swiper .swiper-button-prev:hover {
    background-color: rgba(0, 0, 0, 0.2);
  }
  
  .engine-hours-swiper .swiper-pagination {
    bottom: 0;
  }
  
  .engine-hours-swiper .swiper-pagination-bullet {
    background-color: #888;
    opacity: 0.7;
    width: 12px;
    height: 12px;
    transition: box-shadow 0.2s;
  }
  
  .engine-hours-swiper .swiper-pagination-bullet-active {
    background-color: var(--swiper-pagination-color);
    box-shadow: 0 0 0 2px #fff, 0 0 0 4px rgba(0,0,0,0.15);
    opacity: 1;
  }
  
  html.dark .engine-hours-swiper .swiper-pagination-bullet-active {
    box-shadow: 0 0 0 2px #222, 0 0 0 4px rgba(255,255,255,0.15);
  }
  
  /* チャートエリアでのタッチイベントを優先 */
  .engine-hours-swiper .recharts-wrapper {
    touch-action: manipulation;
    pointer-events: auto;
  }
  
  .engine-hours-swiper .recharts-tooltip-wrapper {
    touch-action: manipulation;
    pointer-events: auto;
    z-index: 1000;
  }
  
  /* Swiperのタッチイベントをチャートエリアでは無効化 */
  .engine-hours-swiper .recharts-wrapper * {
    touch-action: manipulation;
  }
`;

// スタイルを動的に追加
if (typeof document !== 'undefined') {
  // 既存のスタイルを削除
  const existingStyle = document.getElementById('engine-hours-swiper-style');
  if (existingStyle) {
    existingStyle.remove();
  }
  
  const styleElement = document.createElement('style');
  styleElement.id = 'engine-hours-swiper-style';
  styleElement.textContent = swiperStyles;
  document.head.appendChild(styleElement);
} 
